import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Row, Col } from 'reactstrap';
import Table from '../../../../common/Table';
import {
  clearItineraryAdditionalFoods,
  getItineraryAdditionalFoods,
} from '../../../../../actions/itinerary/ItineraryAdditionalFood';
import ResourceProperty from '../../../../common/resource/ResourceProperty';
import Alert from '../../../../common/informative/Alert';
import EnumStatus from '../../../../common/EnumStatus';
import { FOOD_PUBLIC_TYPE } from '../../../../../config/constants';
import { EXPENSE_PATH } from '../../../../../config/paths';

const ItineraryAdditionalFoods = ({
  itineraryId,
  dispatchGetItineraryAdditionalFoods,
  dispatchClearItineraryAdditionalFoods,
  itineraryAdditionalFoods,
  loading,
}) => {
  useEffect(() => {
    if (itineraryId) {
      dispatchGetItineraryAdditionalFoods(Number(itineraryId));
    }

    return () => {
      dispatchClearItineraryAdditionalFoods();
    };
  }, [
    itineraryId,
    dispatchGetItineraryAdditionalFoods,
    dispatchClearItineraryAdditionalFoods,
  ]);

  const renderCorrelative = ({
    expenseDocumentSeries,
    expenseDocumentCode,
  }) => {
    if (!expenseDocumentSeries && !expenseDocumentCode) {
      return '-';
    }

    return `${expenseDocumentSeries || ''}-${expenseDocumentCode || ''}`;
  };

  const renderPrice = (price) => {
    if (price === null || price === undefined) {
      return '-';
    }

    return Number(price).toFixed(2);
  };

  const renderAdditionalFoods = () => {
    if (!itineraryAdditionalFoods || itineraryAdditionalFoods.length === 0) {
      return (
        <Alert
          type="warning"
          message="No hay alimentos adicionales registrados"
        />
      );
    }

    return (
      <>
        {itineraryAdditionalFoods.map((item) => {
          const {
            id,
            supplierId,
            supplierBusinessTaxId,
            supplierName,
            expenseId,
            expenseDocumentSeries,
            expenseDocumentCode,
            comment,
            itineraryAdditionalFoodTypeList,
          } = item;

          const detailList =
            itineraryAdditionalFoodTypeList &&
            itineraryAdditionalFoodTypeList.toJS
              ? itineraryAdditionalFoodTypeList.toJS()
              : itineraryAdditionalFoodTypeList || [];

          return (
            <div key={id}>
              <Row>
                <Col>
                  {supplierId && (
                    <>
                      <ResourceProperty label="Proveedor:">
                        {supplierName || '-'}
                      </ResourceProperty>

                      <ResourceProperty label="RUC:">
                        {supplierBusinessTaxId || '-'}
                      </ResourceProperty>
                    </>
                  )}

                  {expenseId && (
                    <ResourceProperty label="Correlativo:">
                      <Link
                        to={`${EXPENSE_PATH}/${expenseId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {renderCorrelative({
                          expenseDocumentSeries,
                          expenseDocumentCode,
                        })}
                      </Link>
                    </ResourceProperty>
                  )}
                  {!supplierId && !expenseId && (
                    <ResourceProperty label="Origen:">-</ResourceProperty>
                  )}

                  <ResourceProperty label="Comentario:">
                    {comment || '-'}
                  </ResourceProperty>
                </Col>

                <Col>
                  <h4>Detalle</h4>

                  {detailList.length === 0 ? (
                    <Alert
                      type="warning"
                      message="No hay detalle de alimentos"
                    />
                  ) : (
                    <Table
                      columns={[
                        {
                          Header: 'Alimento',
                          accessor: 'food.name',
                          className: 'text-center',
                        },
                        {
                          Header: 'Tipo público',
                          accessor: 'publicType',
                          className: 'text-center',
                          Cell: (row) => (
                            <EnumStatus
                              enumObject={FOOD_PUBLIC_TYPE}
                              enumValue={row.value}
                            />
                          ),
                        },
                        {
                          Header: 'Cantidad',
                          accessor: 'quantity',
                          className: 'text-center',
                        },
                        {
                          Header: 'Precio',
                          accessor: 'price',
                          className: 'text-center',
                          Cell: (row) => renderPrice(row.original.price),
                        },
                      ]}
                      data={detailList}
                      defaultPageSize={detailList.length}
                      showPagination={false}
                    />
                  )}
                </Col>
              </Row>
              <hr />
            </div>
          );
        })}
      </>
    );
  };

  if (loading) {
    return <Alert type="info" message="Cargando alimentos adicionales..." />;
  }

  return renderAdditionalFoods();
};

ItineraryAdditionalFoods.propTypes = {
  itineraryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  dispatchGetItineraryAdditionalFoods: PropTypes.func.isRequired,
  dispatchClearItineraryAdditionalFoods: PropTypes.func.isRequired,
  itineraryAdditionalFoods: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      itineraryId: PropTypes.number,
      supplierId: PropTypes.number,
      supplierBusinessTaxId: PropTypes.string,
      supplierName: PropTypes.string,
      expenseId: PropTypes.number,
      expenseDocumentSeries: PropTypes.string,
      expenseDocumentCode: PropTypes.number,
      comment: PropTypes.string,
      // eslint-disable-next-line react/forbid-prop-types
      itineraryAdditionalFoodTypeList: PropTypes.array,
    }),
  ),
  loading: PropTypes.bool,
};

ItineraryAdditionalFoods.defaultProps = {
  itineraryAdditionalFoods: [],
  loading: false,
};

const mapStateToProps = ({ ItineraryUnit }) => {
  const content = ItineraryUnit.ItineraryAdditionalFood.getIn([
    'all',
    'content',
  ]);

  const contentJs = content && content.toJS ? content.toJS() : content;

  return {
    itineraryAdditionalFoods: Array.isArray(contentJs) ? contentJs : [],
    loading: ItineraryUnit.ItineraryAdditionalFood.getIn(['all', 'loading']),
  };
};

const mapDispatchToProps = {
  dispatchGetItineraryAdditionalFoods: getItineraryAdditionalFoods,
  dispatchClearItineraryAdditionalFoods: clearItineraryAdditionalFoods,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ItineraryAdditionalFoods);
